import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, LogOut, RefreshCw, Inbox, Loader2, BellRing, MessageCircle, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["baru", "diproses", "selesai", "batal"];
const STATUS_COLORS = {
  baru: "bg-red-100 text-[#E60000]",
  diproses: "bg-amber-100 text-amber-700",
  selesai: "bg-green-100 text-green-700",
  batal: "bg-gray-200 text-gray-600",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 font-medium">{label}</p>
    <p className="text-sm text-gray-900 font-medium break-words">{value || "-"}</p>
  </div>
);

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const knownIds = useRef(null);

  const fetchOrders = async (silent = false) => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      const list = ordersRes.data;
      if (knownIds.current !== null) {
        const fresh = list.filter((o) => !knownIds.current.has(o.id));
        if (fresh.length > 0) {
          toast.success(`${fresh.length} order baru masuk!`, {
            icon: <BellRing className="w-4 h-4" />,
            duration: 8000,
          });
        }
      }
      knownIds.current = new Set(list.map((o) => o.id));
      setOrders(list);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      } else if (!silent) {
        toast.error("Gagal memuat order.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin === null) {
      navigate("/admin/login");
      return;
    }
    if (admin) {
      fetchOrders();
      const interval = setInterval(() => fetchOrders(true), 10000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o))
      );
      toast.success("Status order diperbarui.");
    } catch {
      toast.error("Gagal memperbarui status.");
    }
  };

  const waLink = (o) =>
    `https://wa.me/62${o.phone.replace(/\D/g, "").replace(/^0/, "")}?text=${encodeURIComponent(
      `Halo ${o.owner_name}, kami dari RismaCell terkait order migrasi paket retensi untuk nomor IndiHome ${o.indihome_number}.`
    )}`;

  const exportCSV = () => {
    const headers = [
      "Tanggal", "Status", "No IndiHome", "Nama Pemilik", "NIK KTP",
      "Tempat & Tgl Lahir", "Tagihan Terakhir", "Tagihan Bulan Ini",
      "Bayar Lewat", "Jalan", "Desa/Kel", "Kecamatan", "Kota/Kab",
      "Provinsi", "Email", "No HP", "Paket Saat Ini", "Min 6 Bulan",
      "Add-on Kuota Keluarga", "Paket Dituju", "Catatan",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = orders.map((o) =>
      [
        fmtDate(o.created_at), o.status, o.indihome_number, o.owner_name,
        o.nik_ktp, o.birth_place_date, o.last_bill_amount,
        o.bill_paid_this_month, o.bill_payment_method, o.address_street,
        o.address_village, o.address_district, o.address_city,
        o.address_province, o.email, o.phone, o.current_package,
        o.min_6_months, o.addon_kuota_keluarga, o.desired_package, o.notes,
      ].map(esc).join(",")
    );
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-rismacell-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("File CSV order berhasil diunduh.");
  };

  if (admin === undefined || loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-gray-950 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#E60000] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </span>
            <span className="font-heading font-extrabold tracking-tight">
              Admin Risma<span className="text-[#E60000]">Cell</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => fetchOrders()}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Muat ulang"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATUSES.map((s) => (
            <div
              key={s}
              data-testid={`admin-stat-${s}`}
              className="bg-white rounded-2xl border border-black/10 p-5"
            >
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">
                {s}
              </p>
              <p className="font-heading text-3xl font-black text-gray-900 mt-1">
                {stats[s] || 0}
              </p>
            </div>
          ))}
        </div>

        <h1 className="font-heading text-2xl font-bold text-gray-900 mt-10 mb-5">
          Daftar Order
        </h1>

        {orders.length > 0 && (
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCSV}
            className="mb-5 inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gray-700 active:scale-95 transition-[background-color,transform]"
          >
            <Download className="w-4 h-4" />
            Unduh CSV ({orders.length} order)
          </button>
        )}

        {orders.length === 0 ? (
          <div
            data-testid="admin-orders-empty"
            className="bg-white rounded-2xl border border-dashed border-gray-300 p-16 text-center"
          >
            <Inbox className="w-10 h-10 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-4 font-medium">
              Belum ada order masuk.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-orders-list">
            {orders.map((o) => (
              <div
                key={o.id}
                data-testid={`admin-order-card-${o.id}`}
                className="bg-white rounded-2xl border border-black/10 overflow-hidden"
              >
                <button
                  data-testid={`admin-order-toggle-${o.id}`}
                  onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  className="w-full flex flex-wrap items-center gap-4 px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span
                    className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${STATUS_COLORS[o.status] || STATUS_COLORS.baru}`}
                  >
                    {o.status}
                  </span>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-heading font-semibold text-gray-900">
                      {o.owner_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {o.indihome_number} • {fmtDate(o.created_at)}
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-700 hidden md:block">
                    {o.desired_package}
                  </p>
                </button>

                {expanded === o.id && (
                  <div className="border-t border-gray-100 px-6 py-6 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                      <DetailRow label="NIK KTP" value={o.nik_ktp} />
                      <DetailRow label="Tempat & Tgl Lahir" value={o.birth_place_date} />
                      <DetailRow label="Email" value={o.email} />
                      <DetailRow label="No HP" value={o.phone} />
                      <DetailRow label="Tagihan Terakhir" value={`Rp ${o.last_bill_amount}`} />
                      <DetailRow label="Tagihan Bulan Ini" value={o.bill_paid_this_month} />
                      <DetailRow label="Bayar Lewat" value={o.bill_payment_method} />
                      <DetailRow label="Min. 6 Bulan" value={o.min_6_months} />
                      <DetailRow label="Paket Saat Ini" value={o.current_package} />
                      <DetailRow label="Paket Dituju" value={o.desired_package} />
                      <DetailRow label="Add-on Kuota Keluarga" value={o.addon_kuota_keluarga} />
                      <DetailRow label="Catatan" value={o.notes} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">
                        Alamat Pemasangan
                      </p>
                      <p className="text-sm text-gray-900 font-medium">
                        {`${o.address_street}, ${o.address_village}, ${o.address_district}, ${o.address_city}, ${o.address_province}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">
                          Ubah status:
                        </span>
                        <Select
                          value={o.status}
                          onValueChange={(v) => updateStatus(o.id, v)}
                        >
                          <SelectTrigger
                            data-testid={`admin-status-select-${o.id}`}
                            className="w-36 h-9"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <a
                        href={waLink(o)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`admin-wa-link-${o.id}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#25D366] px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat Pelanggan
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
